import { useMutation, useQuery, useQueryClient } from 'react-query';

import type {
  AddManagedUsersResponse,
  ManagedUsersResponse,
} from '../../../../backend/src/organisation/dto/managed-users.dto';
import { APIBaseChronos } from '@/api/hosts';
import { QueryEntity } from '@/api/types';
import useGetFetchConfig, { FetchMethod, useFetcher } from '@/api/useGetFetchConfig';

export function useManagedUsers(organisationId: string) {
  const fetcher = useFetcher();
  const { getFetchConfig } = useGetFetchConfig();
  const client = useQueryClient();
  const key = [QueryEntity.ManagedUsers, organisationId];
  const queryParams = new URLSearchParams({ orgId: organisationId }).toString();
  const url = `${APIBaseChronos}/api/org/managed-users?${queryParams}`;

  const checkResponse = async (response: Response) => {
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(typeof error.message === 'string' ? error.message : 'Could not update users. Please try again.');
    }
    return response;
  };

  const query = useQuery<ManagedUsersResponse>(key, () => fetcher(url), {
    enabled: !!organisationId,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  const add = useMutation<AddManagedUsersResponse, Error, string[]>(
    async (emails) => {
      const response = await checkResponse(
        await fetch(url, getFetchConfig({ method: FetchMethod.POST, data: { emails } })),
      );
      return response.json();
    },
    {
      onSuccess: () => {
        void client.invalidateQueries([QueryEntity.Users]);
      },
    },
  );

  return { query, add };
}
